/**
 * Device SIP bootstrap boundary. Two input shapes are accepted; everything else fails closed
 * (the engine does not connect, register or accept offers):
 *
 * 1. CANONICAL (authority): `Device.bootstrap` from packages/contracts, exactly as B1 returns it from
 *    `POST /api/v1/devices` and `POST /api/v1/devices/{id}/credentials`:
 *    `{status:'unconfigured', reason_code}` or
 *    `{status:'issued', sip_uri, wss_url, username, password, expires_at}`.
 *    A whole `Device` object is also accepted and unwrapped. Values the contract does not carry
 *    (REGISTER expiry, ICE servers, INVITE correlation header names) come from engine
 *    configuration (`CanonicalBootstrapDefaults`), never from guesses about the payload.
 * 2. PROPOSED `bcio.sip_device_bootstrap` v1 (compatibility): the richer document from the first
 *    F2 report. Kept so existing callers keep working; not a contract.
 */
import { isUuid } from './ids.js';

export const SIP_DEVICE_BOOTSTRAP_SCHEMA = 'bcio.sip_device_bootstrap';
export const SIP_DEVICE_BOOTSTRAP_VERSION = 1;
/** An ephemeral device credential. Anything longer is refused as not ephemeral. */
export const MAX_BOOTSTRAP_LIFETIME_MS = 24 * 3600_000;
/** Below this much remaining lifetime the client refreshes instead of registering. */
export const MIN_BOOTSTRAP_REMAINING_MS = 30_000;
/**
 * Canonical credentials carry no refresh time. The client refreshes at CANONICAL_REFRESH_FRACTION of the remaining life,
 * less a random share of up to CANONICAL_REFRESH_JITTER of it so phones issued in the same second do not rotate (and
 * reload the PBX) together: 45-48 minutes into B1's 60-minute credential. The refresh is never later than
 * CANONICAL_REFRESH_LEAD_MS before expiry and never earlier than half-life, so a short credential (one capped by the
 * session's end) keeps the old schedule: 12 minutes of 15, 2.5 of 5.
 */
export const CANONICAL_REFRESH_FRACTION = 0.8;
export const CANONICAL_REFRESH_JITTER = 0.05;
export const CANONICAL_REFRESH_LEAD_MS = 180_000;

/** Milliseconds from receipt to the scheduled refresh of a canonical credential with `remainingMs` left. */
export function canonicalRefreshDelay(remainingMs: number, random: () => number = Math.random): number {
  const draw = random();
  const share = Number.isFinite(draw) ? Math.min(Math.max(draw, 0), 1) : 0;
  const target = Math.floor(remainingMs * (CANONICAL_REFRESH_FRACTION - CANONICAL_REFRESH_JITTER * share));
  return Math.max(Math.floor(remainingMs / 2), Math.min(target, remainingMs - CANONICAL_REFRESH_LEAD_MS));
}
const FUTURE_ISSUE_TOLERANCE_MS = 5 * 60_000;

/** Header names the PBX must add to every server-originated device-leg INVITE (see F2-REPORT). */
export const DEFAULT_CORRELATION_HEADERS = Object.freeze({ callIdHeader: 'X-BCIO-Call-Id', legIdHeader: 'X-BCIO-Leg-Id', legRoleHeader: 'X-BCIO-Leg-Role' });
/** apps/telephony renders browser AORs with default_expiration=120 (minimum 60, maximum 300). */
export const DEFAULT_REGISTER_EXPIRES_SECONDS = 120;

export type CanonicalIssuedBootstrap = {
  status: 'issued';
  sip_uri: string;

  username: string;
  password: string;
  expires_at: string;
} & ({wss_url:string;transport?:never;signaling_url?:never} | {transport:'https_long_poll';signaling_url:string;wss_url?:never});
export type CanonicalBootstrap = { status: 'unconfigured'; reason_code: string } | CanonicalIssuedBootstrap;

export interface SipDeviceBootstrapV1Ready {
  schema: typeof SIP_DEVICE_BOOTSTRAP_SCHEMA;
  version: 1;
  status: 'ready';
  device_id: string;
  issued_at: string;
  expires_at: string;
  refresh_after: string;
  sip: {
    websocket_url: string;
    aor: string;
    authorization_username: string;
    authorization_password: string;
    register_expires_seconds: number;
  };
  ice: {
    servers: Array<{ urls: string[]; username?: string; credential?: string }>;
    transport_policy: 'all' | 'relay';
  };
  correlation: {
    call_id_header: string;
    leg_id_header: string;
    leg_role_header: string;
  };
}

export interface SipDeviceBootstrapV1Unavailable {
  schema: typeof SIP_DEVICE_BOOTSTRAP_SCHEMA;
  version: 1;
  status: 'unconfigured' | 'suspended' | 'revoked';
  device_id: string;
  reason_code: string;
}

export type SipDeviceBootstrapV1 = SipDeviceBootstrapV1Ready | SipDeviceBootstrapV1Unavailable;

export type BootstrapStatus = 'ready' | 'unconfigured' | 'suspended' | 'revoked' | 'expired' | 'invalid' | 'unavailable';

export interface IceServerConfig { readonly urls: readonly string[]; readonly username?: string; readonly credential?: string }

export interface CorrelationHeaders { readonly callIdHeader: string; readonly legIdHeader: string; readonly legRoleHeader: string }

/** Engine-side configuration for fields the canonical contract does not carry. */
export interface CanonicalBootstrapDefaults {
  readonly registerExpiresSeconds: number;
  readonly iceServers: readonly IceServerConfig[];
  readonly iceTransportPolicy: 'all' | 'relay';
  readonly correlation: CorrelationHeaders;
}

export const DEFAULT_CANONICAL_BOOTSTRAP: CanonicalBootstrapDefaults = Object.freeze({
  registerExpiresSeconds: DEFAULT_REGISTER_EXPIRES_SECONDS,
  iceServers: Object.freeze([]),
  iceTransportPolicy: 'all',
  correlation: DEFAULT_CORRELATION_HEADERS,
});

/** Validated, normalized bootstrap. The SIP password is reachable only through `credentials()`. */
export interface ValidatedBootstrap {
  readonly source: 'canonical' | 'proposed_v1';
  readonly deviceId: string;
  readonly issuedAt: number;
  /** Local time the payload was validated; used to avoid rotating fresh credentials. */
  readonly receivedAt: number;
  readonly expiresAt: number;
  readonly refreshAt: number;
  readonly signalingUrl: string;
  readonly transport: 'websocket' | 'https_long_poll';
  /** Present only for the legacy WebSocket transport. */
  readonly websocketUrl?: string;
  readonly aor: string;
  readonly registerExpiresSeconds: number;
  readonly iceServers: readonly IceServerConfig[];
  readonly iceTransportPolicy: 'all' | 'relay';
  readonly correlation: CorrelationHeaders;
  credentials(): { readonly username: string; readonly password: string };
  /** Safe public view; never contains the password or TURN credentials. */
  toJSON(): BootstrapPublicView;
}

export interface BootstrapPublicView {
  readonly transport?: 'https_long_poll';
  readonly status: 'ready';
  readonly source: 'canonical' | 'proposed_v1';
  readonly device_id: string;
  readonly expires_at: string;
  readonly refresh_at: string;
  readonly sip_host: string;
  readonly ice_transport_policy: 'all' | 'relay';
  readonly turn_configured: boolean;
}

export type BootstrapParseResult =
  | { readonly ok: true; readonly bootstrap: ValidatedBootstrap }
  | { readonly ok: false; readonly status: Exclude<BootstrapStatus, 'ready' | 'unavailable'>; readonly reason_code: string };

export interface BootstrapParseOptions {
  readonly deviceId: string;
  readonly now: number;
  /** Exact hostnames this build may connect to for SIP. Configured by the application, never by the payload. */
  readonly allowedSipHosts: readonly string[];
  /** Development/test only: permit `ws://` to 127.0.0.1, ::1 or localhost. */
  readonly allowInsecureLoopback?: boolean;
  readonly canonicalDefaults?: Partial<CanonicalBootstrapDefaults>;
  /** Jitter source for the canonical refresh time, in [0, 1). Defaults to Math.random. */
  readonly random?: () => number;
}

const LOOPBACK = new Set(['127.0.0.1', 'localhost', '[::1]', '::1']);
const SAFE_TOKEN = /^[A-Za-z0-9_-]+$/;
const REASON = /^[a-z0-9_]{1,100}$/;
const HEADER = /^X-[A-Za-z0-9-]{1,60}$/;
const ICE_URL = /^(stun|turn|turns):[^\s]{1,250}$/;
const SIP_AOR = /^sip:([A-Za-z0-9_-]{1,64})@([A-Za-z0-9.-]{1,253})$/;

const invalid = (reason_code: string): BootstrapParseResult => ({ ok: false, status: 'invalid', reason_code });
const expired: BootstrapParseResult = { ok: false, status: 'expired', reason_code: 'bootstrap_expired' };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && Object.getPrototypeOf(value) === Object.prototype;
}

function exactKeys(value: Record<string, unknown>, required: readonly string[], optional: readonly string[] = []): boolean {
  const keys = Object.keys(value);
  return required.every(k => Object.hasOwn(value, k)) && keys.every(k => required.includes(k) || optional.includes(k));
}

function time(value: unknown): number | null {
  if (typeof value !== 'string' || value.length > 40 || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,6})?(Z|[+-]\d{2}:\d{2})$/.test(value)) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function hostAllowed(host: string, options: BootstrapParseOptions): boolean {
  const normalized = host.toLowerCase();
  if (options.allowInsecureLoopback && LOOPBACK.has(normalized)) return true;
  return options.allowedSipHosts.some(allowed => allowed.toLowerCase() === normalized);
}

function checkWebSocket(value: unknown, options: BootstrapParseOptions): URL | string {
  if (typeof value !== 'string' || value.length > 2048) return 'bootstrap_ws_url_invalid';
  let ws: URL;
  try { ws = new URL(value); } catch { return 'bootstrap_ws_url_invalid'; }
  const loopback = LOOPBACK.has(ws.hostname.toLowerCase());
  if (!(ws.protocol === 'wss:' || (ws.protocol === 'ws:' && options.allowInsecureLoopback === true && loopback))) return 'bootstrap_ws_insecure';
  if (ws.username || ws.password || ws.search || ws.hash) return 'bootstrap_ws_url_invalid';
  if (!hostAllowed(ws.hostname, options)) return 'bootstrap_ws_host_not_allowed';
  return ws;
}

function checkHttp(value:unknown, options:BootstrapParseOptions):URL|string {
  if(typeof value!=='string'||value!==`https://sidet.one/api/v1/telephony/http/${options.deviceId.toLowerCase()}`||!hostAllowed('sidet.one',options))return 'bootstrap_http_url_invalid';
  return new URL(value);
}

function checkIdentity(aorValue: unknown, user: unknown, password: unknown, options: BootstrapParseOptions): { user: string; password: string; aor: string } | string {
  if (typeof user !== 'string' || user.length < 1 || user.length > 64 || !SAFE_TOKEN.test(user)) return 'bootstrap_username_invalid';
  if (typeof password !== 'string' || password.length < 32 || password.length > 128 || !SAFE_TOKEN.test(password)) return 'bootstrap_password_invalid';
  if (typeof aorValue !== 'string') return 'bootstrap_aor_invalid';
  const aor = SIP_AOR.exec(aorValue);
  if (!aor || aor[1] !== user) return 'bootstrap_aor_invalid';
  if (!hostAllowed(aor[2]!, options)) return 'bootstrap_aor_host_not_allowed';
  return { user, password, aor: aorValue };
}

function checkDefaults(defaults: CanonicalBootstrapDefaults): string | null {
  const expires = defaults.registerExpiresSeconds;
  if (!Number.isInteger(expires) || expires < 60 || expires > 300) return 'bootstrap_register_expires_invalid';
  const c = defaults.correlation;
  const headers = [c.callIdHeader, c.legIdHeader, c.legRoleHeader];
  if (!headers.every(h => HEADER.test(h)) || new Set(headers.map(h => h.toLowerCase())).size !== 3) return 'bootstrap_correlation_invalid';
  if (defaults.iceServers.length > 4 || defaults.iceServers.some(s => s.urls.length < 1 || !s.urls.every(u => ICE_URL.test(u)))) return 'bootstrap_ice_invalid';
  if (defaults.iceTransportPolicy === 'relay' && !defaults.iceServers.some(s => s.urls.some(u => u.startsWith('turn')))) return 'bootstrap_relay_without_turn';
  return null;
}

interface BuildInput {
  source: ValidatedBootstrap['source'];
  deviceId: string;
  issuedAt: number;
  receivedAt: number;
  expiresAt: number;
  refreshAt: number;
  ws: URL;
  transport?: 'https_long_poll';
  identity: { user: string; password: string; aor: string };
  registerExpiresSeconds: number;
  iceServers: readonly IceServerConfig[];
  iceTransportPolicy: 'all' | 'relay';
  correlation: CorrelationHeaders;
}

function build(input: BuildInput): ValidatedBootstrap {
  const servers = Object.freeze(input.iceServers.map(s => Object.freeze({
    urls: Object.freeze([...s.urls]),
    ...(s.username !== undefined ? { username: s.username } : {}),
    ...(s.credential !== undefined ? { credential: s.credential } : {}),
  })));
  const view: BootstrapPublicView = Object.freeze({
    status: 'ready',
    source: input.source,
    device_id: input.deviceId,
    expires_at: new Date(input.expiresAt).toISOString(),
    refresh_at: new Date(input.refreshAt).toISOString(),
    ...(input.transport ? {transport:input.transport} : {}),
    sip_host: input.ws.hostname.toLowerCase(),
    ice_transport_policy: input.iceTransportPolicy,
    turn_configured: servers.some(s => s.urls.some(u => u.startsWith('turn'))),
  });
  const secret = Object.freeze({ username: input.identity.user, password: input.identity.password });
  return Object.freeze({
    source: input.source,
    deviceId: input.deviceId,
    issuedAt: input.issuedAt,
    receivedAt: input.receivedAt,
    expiresAt: input.expiresAt,
    refreshAt: input.refreshAt,
    signalingUrl: input.ws.toString(),
    transport: input.transport ?? 'websocket',
    ...(input.transport ? {} : {websocketUrl: input.ws.toString()}),
    aor: input.identity.aor,
    registerExpiresSeconds: input.registerExpiresSeconds,
    iceServers: servers,
    iceTransportPolicy: input.iceTransportPolicy,
    correlation: Object.freeze({ ...input.correlation }),
    credentials: () => secret,
    toJSON: () => view,
  });
}

/** Parses a payload. Unknown keys, versions or fields fail closed, including stray carrier/API keys. */
export function parseSipDeviceBootstrap(input: unknown, options: BootstrapParseOptions): BootstrapParseResult {
  let raw = input;
  if (!isRecord(raw)) return invalid('bootstrap_not_object');

  // A whole canonical Device (POST /devices, POST /devices/{id}/credentials): unwrap its bootstrap.
  if (Object.hasOwn(raw, 'user_id') && Object.hasOwn(raw, 'id') && !Object.hasOwn(raw, 'schema')) {
    if (typeof raw.id !== 'string' || raw.id.toLowerCase() !== options.deviceId.toLowerCase()) return invalid('bootstrap_device_mismatch');
    if (raw.revoked_at !== null && raw.revoked_at !== undefined) return { ok: false, status: 'revoked', reason_code: 'device_revoked' };
    if (raw.bootstrap === undefined) return { ok: false, status: 'unconfigured', reason_code: 'bootstrap_not_issued' };
    raw = raw.bootstrap;
    if (!isRecord(raw)) return invalid('bootstrap_not_object');
  }

  if (!Object.hasOwn(raw, 'schema')) {
    if (raw.status === 'unconfigured' && exactKeys(raw, ['status', 'reason_code']) && typeof raw.reason_code === 'string') {
      return { ok: false, status: 'unconfigured', reason_code: REASON.test(raw.reason_code) ? raw.reason_code : 'unconfigured' };
    }
    if (raw.status === 'issued') return parseCanonicalIssued(raw, options);
    return invalid('bootstrap_schema_missing');
  }
  return parseProposedV1(raw, options);
}

function parseCanonicalIssued(raw: Record<string, unknown>, options: BootstrapParseOptions): BootstrapParseResult {
  const http = raw.transport === 'https_long_poll';
  if (!exactKeys(raw, http ? ['status','transport','sip_uri','signaling_url','username','password','expires_at'] : ['status', 'sip_uri', 'wss_url', 'username', 'password', 'expires_at'])) return invalid('bootstrap_fields_invalid');
  const expiresAt = time(raw.expires_at);
  if (expiresAt === null) return invalid('bootstrap_time_invalid');
  const remaining = expiresAt - options.now;
  if (remaining < MIN_BOOTSTRAP_REMAINING_MS) return expired;
  if (remaining > MAX_BOOTSTRAP_LIFETIME_MS) return invalid('bootstrap_lifetime_invalid');
  const ws = http ? checkHttp(raw.signaling_url, options) : checkWebSocket(raw.wss_url, options);
  if (typeof ws === 'string') return invalid(ws);
  const identity = checkIdentity(raw.sip_uri, raw.username, raw.password, options);
  if (typeof identity === 'string') return invalid(identity);
  const defaults: CanonicalBootstrapDefaults = { ...DEFAULT_CANONICAL_BOOTSTRAP, ...options.canonicalDefaults };
  const bad = checkDefaults(defaults);
  if (bad) return invalid(bad);
  return {
    ok: true,
    bootstrap: build({
      source: 'canonical',
      ...(http ? {transport:'https_long_poll' as const} : {}),
      deviceId: options.deviceId.toLowerCase(),
      issuedAt: options.now,
      receivedAt: options.now,
      expiresAt,
      refreshAt: options.now + canonicalRefreshDelay(remaining, options.random),
      ws,
      identity,
      registerExpiresSeconds: defaults.registerExpiresSeconds,
      iceServers: defaults.iceServers,
      iceTransportPolicy: defaults.iceTransportPolicy,
      correlation: defaults.correlation,
    }),
  };
}

function parseProposedV1(raw: Record<string, unknown>, options: BootstrapParseOptions): BootstrapParseResult {
  if (raw.schema !== SIP_DEVICE_BOOTSTRAP_SCHEMA) return invalid('bootstrap_schema_unknown');
  if (raw.version !== SIP_DEVICE_BOOTSTRAP_VERSION) return invalid('bootstrap_version_unsupported');
  if (typeof raw.device_id !== 'string' || !isUuid(raw.device_id) || raw.device_id.toLowerCase() !== options.deviceId.toLowerCase()) {
    return invalid('bootstrap_device_mismatch');
  }
  if (raw.status === 'unconfigured' || raw.status === 'suspended' || raw.status === 'revoked') {
    if (!exactKeys(raw, ['schema', 'version', 'status', 'device_id', 'reason_code']) || typeof raw.reason_code !== 'string' || !REASON.test(raw.reason_code)) {
      return invalid('bootstrap_fields_invalid');
    }
    return { ok: false, status: raw.status, reason_code: raw.reason_code };
  }
  if (raw.status !== 'ready') return invalid('bootstrap_status_unknown');
  if (!exactKeys(raw, ['schema', 'version', 'status', 'device_id', 'issued_at', 'expires_at', 'refresh_after', 'sip', 'ice', 'correlation'])) {
    return invalid('bootstrap_fields_invalid');
  }

  const issuedAt = time(raw.issued_at), expiresAt = time(raw.expires_at), refreshAfter = time(raw.refresh_after);
  if (issuedAt === null || expiresAt === null || refreshAfter === null) return invalid('bootstrap_time_invalid');
  if (expiresAt <= issuedAt || expiresAt - issuedAt > MAX_BOOTSTRAP_LIFETIME_MS) return invalid('bootstrap_lifetime_invalid');
  if (refreshAfter < issuedAt || refreshAfter > expiresAt) return invalid('bootstrap_refresh_invalid');
  if (issuedAt > options.now + FUTURE_ISSUE_TOLERANCE_MS) return invalid('bootstrap_issued_in_future');
  // Bound expiry by both the stated time and the stated lifetime measured from receipt.
  const localExpiry = Math.min(expiresAt, options.now + (expiresAt - issuedAt));
  if (localExpiry - options.now < MIN_BOOTSTRAP_REMAINING_MS) return expired;
  const localRefresh = Math.min(localExpiry - MIN_BOOTSTRAP_REMAINING_MS / 2, Math.max(options.now, options.now + (refreshAfter - issuedAt)));

  const sip = raw.sip;
  if (!isRecord(sip) || !exactKeys(sip, ['websocket_url', 'aor', 'authorization_username', 'authorization_password', 'register_expires_seconds'])) {
    return invalid('bootstrap_sip_fields_invalid');
  }
  const ws = checkWebSocket(sip.websocket_url, options);
  if (typeof ws === 'string') return invalid(ws);
  const identity = checkIdentity(sip.aor, sip.authorization_username, sip.authorization_password, options);
  if (typeof identity === 'string') return invalid(identity);
  const expires = sip.register_expires_seconds;
  if (typeof expires !== 'number' || !Number.isInteger(expires) || expires < 60 || expires > 300) return invalid('bootstrap_register_expires_invalid');

  const ice = raw.ice;
  if (!isRecord(ice) || !exactKeys(ice, ['servers', 'transport_policy'])) return invalid('bootstrap_ice_invalid');
  if (ice.transport_policy !== 'all' && ice.transport_policy !== 'relay') return invalid('bootstrap_ice_invalid');
  if (!Array.isArray(ice.servers) || ice.servers.length > 4) return invalid('bootstrap_ice_invalid');
  const servers: IceServerConfig[] = [];
  for (const entry of ice.servers as unknown[]) {
    if (!isRecord(entry) || !exactKeys(entry, ['urls'], ['username', 'credential'])) return invalid('bootstrap_ice_invalid');
    if (!Array.isArray(entry.urls) || entry.urls.length < 1 || entry.urls.length > 4) return invalid('bootstrap_ice_invalid');
    const urls = entry.urls as unknown[];
    if (!urls.every((u): u is string => typeof u === 'string' && ICE_URL.test(u))) return invalid('bootstrap_ice_invalid');
    const relay = urls.some(u => u.startsWith('turn'));
    const username = entry.username, credential = entry.credential;
    if (username !== undefined && (typeof username !== 'string' || username.length > 256)) return invalid('bootstrap_ice_invalid');
    if (credential !== undefined && (typeof credential !== 'string' || credential.length > 256)) return invalid('bootstrap_ice_invalid');
    if (relay && (typeof username !== 'string' || typeof credential !== 'string')) return invalid('bootstrap_turn_credentials_missing');
    servers.push({ urls, ...(typeof username === 'string' ? { username } : {}), ...(typeof credential === 'string' ? { credential } : {}) });
  }
  if (ice.transport_policy === 'relay' && !servers.some(s => s.urls.some(u => u.startsWith('turn')))) return invalid('bootstrap_relay_without_turn');

  const correlation = raw.correlation;
  if (!isRecord(correlation) || !exactKeys(correlation, ['call_id_header', 'leg_id_header', 'leg_role_header'])) return invalid('bootstrap_correlation_invalid');
  const headers = [correlation.call_id_header, correlation.leg_id_header, correlation.leg_role_header];
  if (!headers.every((h): h is string => typeof h === 'string' && HEADER.test(h))) return invalid('bootstrap_correlation_invalid');
  if (new Set(headers.map(h => h.toLowerCase())).size !== 3) return invalid('bootstrap_correlation_invalid');

  return {
    ok: true,
    bootstrap: build({
      source: 'proposed_v1',
      deviceId: raw.device_id.toLowerCase(),
      issuedAt,
      receivedAt: options.now,
      expiresAt: localExpiry,
      refreshAt: localRefresh,
      ws,
      identity,
      registerExpiresSeconds: expires,
      iceServers: servers,
      iceTransportPolicy: ice.transport_policy,
      correlation: { callIdHeader: headers[0]!, legIdHeader: headers[1]!, legRoleHeader: headers[2]! },
    }),
  };
}
